// AB#2040 — a searchable, loader-driven FK combobox: the replacement for the plain <select> the shared
// LookupRefField used to render. That select was fed by ONE options fetch the host capped at 200 rows, so
// any referenced entity with >200 records made rows 201+ silently unselectable. This combobox instead
// searches SERVER-SIDE through the host loader on a debounce, so the full referenced set is reachable no
// matter how large it is.
//
// It is deliberately host-agnostic: it takes an async `loadOptions(searchTerm)` and an optional
// `resolveLabel(value)` (both provided by LookupRefField, wired from the renderer seams), so this file
// stays free of any tenant data-fetch coupling and renders identically in the designer preview (whose
// static loader simply ignores the search term).
//
// Behaviour: collapsed it shows the current selection's label (resolved via resolveLabel when the record
// isn't in the current search page); open it debounces (~300ms) a server search, shows a loading row, a
// "no matches" row, keyboard navigation (↑/↓/Enter/Escape) and — for an optional field — a clear button.
import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, type KeyboardEvent as ReactKeyboardEvent, type CSSProperties } from 'react'

export interface LookupOption {
    value: string
    label: string
    /**
     * AB#2543 — the source record's id, when the host loader supplies it. Present-by-coincidence for an
     * id-valued lookup (where it IS the value) and absent exactly where a host needs it most: a
     * code-valued picker submits a CODE, and the id it came from was previously projected away.
     * Optional so every existing loader still satisfies the type.
     */
    id?: string
}

export interface AsyncLookupComboboxProps {
    /**
     * AB#3039 — offer the typed text itself as a choice when nothing matches. For a code-valued picker
     * whose codes belong to an external system, an empty result is not proof the code is wrong; it may
     * simply mean this tenant stores no local record for it. Off by default, so every existing picker
     * keeps refusing values it cannot resolve.
     */
    allowFreeText?: boolean | undefined
    /** The currently selected value (the stored FK id), '' when unset. */
    value: string
    onChange: (value: string) => void
    /**
     * AB#2543 — reports the option the USER picked, so a host can keep the record id alongside the
     * submitted value ("carry both"). Fired only on a real pick or an explicit clear — never on
     * keystrokes, and never from the label-resolution effect, which is not a user action.
     *
     * Advisory, not authoritative: `onChange` remains the source of truth for the stored value. A host
     * MUST discard a reported pick once the field's current value no longer equals the reported
     * `value` — the combobox's value can change without any onChange of its own (index-keyed line rows,
     * copyFrom seeds, a draft reload).
     */
    onPick?: ((picked: LookupOption | undefined) => void) | undefined
    /** Server-side search through the host loader. The empty term loads the initial (unfiltered) page. */
    loadOptions: (searchTerm: string) => Promise<LookupOption[]>
    /**
     * Resolves the label for the current value when it is NOT in the loaded page (fetch-by-id fallback).
     * Absent (or resolving to undefined) → the collapsed control shows the raw value, never a blank.
     */
    resolveLabel?: ((value: string) => Promise<string | undefined>) | undefined
    disabled?: boolean | undefined
    /** Optional fields get a clear (×) button; required fields must always hold a value. */
    required?: boolean | undefined
    placeholder?: string | undefined
    ariaLabel?: string | undefined
    /** Debounce for the server search, ms. */
    debounceMs?: number | undefined
    /**
     * AB#2598 — offers a "create one" row inside the dropdown when the field declares `allowCreate`.
     *
     * Resolves to the value to select (the newly created option), or undefined when the user backs out.
     * The host owns what creating MEANS — this component only offers the affordance and adopts whatever
     * comes back, so the same declaration works for any source without the combobox learning about any.
     *
     * It exists because AB#2598 makes a document type's number range mandatory while a fresh tenant has
     * no document ranges at all: without a way to make one here, a required field would be unfillable
     * and no document type could be created. That is the general case worth naming — an option set the
     * user may legitimately have none of yet.
     */
    onCreate?: (() => Promise<string | undefined>) | undefined
    /** Label for the create row. Defaults to "Create new…". */
    createLabel?: string | undefined
}

const INPUT_CLS = 'w-full rounded border border-neutral-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-neutral-900 dark:text-slate-100 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-neutral-50 disabled:text-neutral-500 dark:disabled:bg-slate-900 dark:disabled:text-slate-500'

/**
 * AB#2040 — the async, loader-driven FK combobox (see file header). Collapsed → the current selection's
 * label; open → a debounced server search with keyboard navigation and a clear affordance.
 */
/**
 * AB#3039 — the typed value to offer as a choice, or null for no offer.
 *
 * Three conditions, and each one is deliberate. The field must DECLARE that a free-typed value is
 * acceptable (off by default, so no existing picker starts accepting unresolvable values). The search
 * must have come back EMPTY — while real options are on screen they are the better answer, and an extra
 * row inviting a typed one would only invite typos. And the user must have typed something, so an
 * untouched picker offers nothing.
 *
 * Trimmed because a trailing space in a code is never what was meant.
 */
export function freeTextOfferFor(
    allowFreeText: boolean | undefined, optionCount: number, query: string,
): string | null {
    if (!allowFreeText || optionCount > 0) { return null }
    return query.trim() || null
}

export function AsyncLookupCombobox({
    value, onChange, onPick, loadOptions, resolveLabel, disabled, required, placeholder, ariaLabel, debounceMs = 300,
    onCreate, createLabel = 'Create new…', allowFreeText = false,
}: AsyncLookupComboboxProps) {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [optionResult, setOptionResult] = useState(() => ({ loader: loadOptions, items: [] as LookupOption[] }))
    const options = useMemo(() => optionResult.loader === loadOptions ? optionResult.items : [], [optionResult, loadOptions])
    const [loading, setLoading] = useState(false)
    // AB#2465 — a rejected search is NOT the same as an empty one. Both used to render "No matches",
    // so a 500 from the backend was indistinguishable from a genuine no-result and could only be
    // diagnosed from server logs. The degrade posture is unchanged (the field stays usable) — only
    // its visibility.
    const [unavailable, setUnavailable] = useState(false)

    const freeTextOffer = freeTextOfferFor(allowFreeText, options.length, query)
    const [highlight, setHighlight] = useState(0)
    // The resolved label for the current value when it isn't in `options` (fetch-by-id fallback).
    const [labelCache, setLabelCache] = useState(() => ({ loadOptions, resolveLabel, labels: new Map<string, string>() }))
    const rememberLabel = useCallback((id: string, label: string) => {
        setLabelCache(previous => {
            const next = new Map(previous.loadOptions === loadOptions && previous.resolveLabel === resolveLabel ? previous.labels : undefined)
            next.delete(id)
            next.set(id, label)
            if (next.size > 50) next.delete(next.keys().next().value!)
            return { loadOptions, resolveLabel, labels: next }
        })
    }, [loadOptions, resolveLabel])

    const containerRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // AB#2649 — the open listbox is position:FIXED at coordinates taken from the trigger, not
    // position:absolute in flow. An absolute dropdown inside any overflow container (every child grid
    // wraps its table in overflow-x-auto, which forces overflow-y auto alongside it) is clipped into
    // the scroll context: the panel grows a scrollbar and the user scrolls the GRID to see the
    // options. Fixed positioning escapes the clip; the rect re-computes on capture-phase scroll (so
    // scrolling ANY ancestor repositions, not just the window) and on resize, and the list flips
    // above the trigger when the viewport bottom is too near for the max list height.
    const [listPos, setListPos] = useState<CSSProperties | null>(null)
    const MaxListPx = 240 // keep in step with the max-h-60 class below
    const MinPanelPx = 280 // AB#3171 — below this a code plus its label is not readable; flip instead
    const Margin = 8 // breathing room kept between the panel and the viewport edge

    const updateListPos = useCallback(() => {
        const anchor = containerRef.current?.getBoundingClientRect()
        if (!anchor) { return }
        const below = window.innerHeight - anchor.bottom
        const openUp = below < MaxListPx + 8 && anchor.top > below
        const vertical = openUp
            ? { bottom: window.innerHeight - anchor.top + 4 }
            : { top: anchor.bottom + 4 }

        // AB#3171 — the trigger's width is a MINIMUM, not the width. Pinning the panel to it made the
        // dropdown exactly one table column wide inside a line grid, so every option rendered as its
        // first character plus an ellipsis ("M…", "T…") and the user could not read what they were
        // choosing. The panel now shrink-wraps its options, floored at the trigger width so it never
        // looks detached, and ceilinged at the space available so it cannot leave the viewport. The
        // options keep `truncate`, which now fires only against that ceiling rather than against a
        // column: content decides the width, the viewport decides the limit.
        //
        // The horizontal flip mirrors the vertical one above: a trigger in the last column has no room
        // to its right, so the panel is anchored by its RIGHT edge and grows leftwards instead.
        const spaceRight = window.innerWidth - anchor.left - Margin
        const spaceLeft = anchor.right - Margin
        const growLeft = spaceRight < MinPanelPx && spaceLeft > spaceRight
        const horizontal = growLeft
            ? { right: window.innerWidth - anchor.right, maxWidth: Math.max(anchor.width, spaceLeft) }
            : { left: anchor.left, maxWidth: Math.max(anchor.width, spaceRight) }

        setListPos({ ...vertical, ...horizontal, minWidth: anchor.width })
    }, [])

    // Layout effect so the first open paints at the right place rather than flashing at 0,0.
    useLayoutEffect(() => {
        if (!open) { setListPos(null); return }
        updateListPos()
        window.addEventListener('scroll', updateListPos, true)
        window.addEventListener('resize', updateListPos)
        return () => {
            window.removeEventListener('scroll', updateListPos, true)
            window.removeEventListener('resize', updateListPos)
        }
    }, [open, updateListPos])

    // The label shown collapsed: prefer a loaded option matching the value, then the resolved label,
    // then the raw value (never blank when a value is set).
    const optionLabel = useMemo(() => options.find(o => o.value === value)?.label, [options, value])
    const cachedLabel = labelCache.loadOptions === loadOptions && labelCache.resolveLabel === resolveLabel ? labelCache.labels.get(value) : undefined
    const collapsedLabel = optionLabel ?? cachedLabel ?? (value || '')

    // AB#2040 — resolve the current value's label when we don't have it (it isn't in the current search
    // page). Uses the host's fetch-by-id fallback; skips the call when a loaded option already covers it,
    // or when there is no value / no resolver (the collapsed control then shows the raw value).
    useEffect(() => {
        if (!value || optionLabel || !resolveLabel) { return }
        let ignore = false
        resolveLabel(value)
            .then(label => { if (!ignore && label) { rememberLabel(value, label) } })
            .catch(() => { /* keep the raw value on error */ })
        return () => { ignore = true }
    }, [value, optionLabel, resolveLabel, rememberLabel])

    // AB#2040 — debounced server search: runs whenever the query changes while open. The empty query
    // (just-opened) loads the initial page so the user sees options before typing.
    const runSearch = useCallback((term: string) => {
        let ignore = false
        setLoading(true)
        loadOptions(term)
            .then(opts => { if (!ignore) { setOptionResult({ loader: loadOptions, items: opts }); setHighlight(0); setUnavailable(false) } })
            .catch(() => { if (!ignore) { setOptionResult({ loader: loadOptions, items: [] }); setUnavailable(true) } })
            .finally(() => { if (!ignore) { setLoading(false) } })
        return () => { ignore = true }
    }, [loadOptions])

    useEffect(() => {
        if (!open) { return }
        if (debounceRef.current) { clearTimeout(debounceRef.current) }
        let cancel: (() => void) | undefined
        debounceRef.current = setTimeout(() => { cancel = runSearch(query) }, debounceMs)
        return () => {
            if (debounceRef.current) { clearTimeout(debounceRef.current) }
            cancel?.()
        }
    }, [open, query, debounceMs, runSearch])

    // Close when clicking outside.
    useEffect(() => {
        if (!open) { return }
        function handleClick(e: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false)
                setQuery('')
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [open])

    // Keep the highlighted row scrolled into view. (Guarded for jsdom, where scrollIntoView is absent.)
    useEffect(() => {
        if (!open || !listRef.current) { return }
        const el = listRef.current.children[highlight] as HTMLElement | undefined
        if (el && typeof el.scrollIntoView === 'function') { el.scrollIntoView({ block: 'nearest' }) }
    }, [highlight, open])

    const select = (opt: LookupOption) => {
        // AB#2543 — onPick fires BEFORE onChange, deliberately. The host that needs the id consumes it
        // synchronously INSIDE its own onChange handler (ViewRenderer's field slots call applyCopyFrom
        // on the same tick), so reporting the pick afterwards would arrive one call too late to be
        // usable. Both values come off the same `opt` in the same synchronous call, so the ordering
        // cannot let the value and the id diverge.
        onPick?.(opt)
        onChange(opt.value)
        rememberLabel(opt.value, opt.label)
        setOpen(false)
        setQuery('')
    }

    const clear = () => {
        // Clearing must report too, or a host keeps a stale id for a field that no longer has a value.
        onPick?.(undefined)
        onChange('')
        setQuery('')
        inputRef.current?.focus()
    }

    const openPicker = () => {
        if (disabled) { return }
        setOpen(true)
        setQuery('')
    }

    const onKeyDown = (e: ReactKeyboardEvent) => {
        if (e.key === 'Escape') {
            if (open) { e.preventDefault(); setOpen(false); setQuery('') }
            return
        }
        if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
            e.preventDefault(); openPicker(); return
        }
        if (!open) { return }
        if (e.key === 'ArrowDown') {
            e.preventDefault()
            setHighlight(h => Math.min(h + 1, Math.max(options.length - 1, 0)))
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setHighlight(h => Math.max(h - 1, 0))
        } else if (e.key === 'Enter') {
            e.preventDefault()
            const opt = options[highlight]
            if (opt) { select(opt) }
        }
    }

    return (
        <div ref={containerRef} className="relative">
            <div className="relative">
                <input
                    ref={inputRef}
                    type="text"
                    role="combobox"
                    aria-label={ariaLabel ?? placeholder ?? 'Search'}
                    aria-expanded={open}
                    aria-autocomplete="list"
                    // Collapsed shows the selection's label; open shows the live query.
                    value={open ? query : collapsedLabel}
                    disabled={disabled}
                    placeholder={placeholder ?? 'Search…'}
                    className={`${INPUT_CLS} ${!required && value && !open ? 'pr-7' : ''}`}
                    autoComplete="off"
                    onFocus={openPicker}
                    onChange={e => { setOpen(true); setQuery(e.target.value) }}
                    onKeyDown={onKeyDown}
                />
                {/* Clear button — optional fields only, when a value is set and the picker is collapsed. */}
                {!required && value && !open && !disabled && (
                    <button
                        type="button"
                        onClick={clear}
                        aria-label="Clear selection"
                        className="absolute inset-y-0 right-0 flex items-center pr-2 text-neutral-400 hover:text-neutral-600"
                    >
                        ×
                    </button>
                )}
            </div>

            {open && (
                <ul
                    ref={listRef}
                    role="listbox"
                    // AB#2649 — fixed + measured coordinates (see updateListPos). Still inside this
                    // component's DOM subtree, so the outside-click close and any enclosing modal's
                    // stacking context behave exactly as before.
                    style={listPos ?? undefined}
                    className="fixed z-50 max-h-60 overflow-y-auto rounded-md border border-neutral-200 dark:border-slate-600 bg-white dark:bg-slate-800 shadow-lg text-sm"
                >
                    {loading ? (
                        <li className="px-3 py-2 text-xs text-neutral-400 dark:text-slate-500 italic">Searching…</li>
                    ) : unavailable ? (
                        <li className="px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
                            Search unavailable — carry on typing the details by hand.
                        </li>
                    ) : options.length === 0 && !onCreate && !freeTextOffer ? (
                        <li className="px-3 py-2 text-xs text-neutral-400 dark:text-slate-500 italic">No matches</li>
                    ) : options.map((opt, i) => (
                        <li
                            key={opt.value}
                            role="option"
                            aria-selected={opt.value === value}
                            onMouseDown={e => { e.preventDefault(); select(opt) }}
                            onMouseEnter={() => setHighlight(i)}
                            className={`px-3 py-2 cursor-pointer truncate ${i === highlight ? 'bg-blue-50 dark:bg-slate-700' : ''} ${opt.value === value ? 'font-medium text-neutral-900 dark:text-slate-100' : 'text-neutral-700 dark:text-slate-300'}`}
                        >
                            {opt.label}
                        </li>
                    ))}

                    {/* AB#3039 — the typed value offered as a choice. Shown only when the search came
                        back empty: while there ARE matches, the codes on screen are the better answer and
                        an extra row inviting a free-typed one would encourage typos. Placed like the
                        create row, and it replaces "No matches" rather than sitting under it, because on
                        a tenant that holds none of these records "No matches" is the permanent state. */}
                    {freeTextOffer && !loading && !unavailable && (
                        <li
                            role="option"
                            aria-selected={false}
                            onMouseDown={e => {
                                e.preventDefault()
                                onChange(freeTextOffer)
                                setOpen(false)
                                setQuery('')
                            }}
                            className="px-3 py-2 cursor-pointer text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700"
                        >
                            Use “{freeTextOffer}”
                        </li>
                    )}

                    {/* AB#2598 — the create row sits BELOW the options and is always offered when the
                        field declares it, not only on an empty list: needing a new one is not the same
                        as finding no match for what you typed. An empty list is the common case on a
                        fresh tenant, which is why this replaces "No matches" rather than sitting under it. */}
                    {onCreate && !loading && !unavailable && (
                        <li
                            role="option"
                            aria-selected={false}
                            onMouseDown={async e => {
                                e.preventDefault()
                                const created = await onCreate()
                                if (created !== undefined && created !== '') {
                                    onChange(created)
                                    setOpen(false)
                                    setQuery('')
                                }
                            }}
                            className={`px-3 py-2 cursor-pointer text-blue-700 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700 ${options.length > 0 ? 'border-t border-neutral-100 dark:border-slate-700' : ''}`}
                        >
                            + {createLabel}
                        </li>
                    )}
                </ul>
            )}
        </div>
    )
}
